import { DragAssignmentItem } from 'components/CalendarDragContext';
import { DropdownOption } from 'components/Dropdown';

import CalculatedStudentAssignmentStatus from 'enums/CalculatedStudentAssignmentStatus';
import CalendarView from 'enums/CalendarView';
import TimeframePreset from 'enums/TimeframePreset';
import UserRole from 'enums/UserRole';

import {
  AssignmentTypeWeightInfoFragment,
  CourseCardInfoFragment,
  CourseOptionFragment,
  HomeStudentAssignmentFragment,
  PlanningAssignmentBaseFragment,
  PlanningStudentCourseFragment,
  StudentOptionFragment,
  UserSettingsFragment,
} from 'graphql/types/generated';
import {
  Courses,
  Plan_Tier_Enum,
  Student_Assignment_Status_Enum,
} from 'graphql/types/schema';

export type PlanningCourse = Pick<
  Courses,
  'id' | 'name' | 'color' | 'numberOfDaysPerWeek' | 'numberOfWeeks'
> | null;

export enum PlanningTabIndex {
  DayWeek,
  Unscheduled,
  All,
}

export interface PlanningState {
  course: PlanningCourse;
  assignments: PlanningAssignmentBaseFragment[] | null;
  studentCourses: PlanningStudentCourseFragment[] | null;
  assignmentTypeWeights: AssignmentTypeWeightInfoFragment[];
  selectedAssignmentId: string | null;
  isCreatingNewAssignment: boolean;
  keepAssignmentSelected: boolean;
  activeTabIndex: PlanningTabIndex;
  currentDay: number;
  currentWeek: number;
  searchPattern: string;
  isSidebarSlideMenuOpen: boolean;
  parrotRequestId: string | null;
  bulkSelectedAssignmentsMap: Record<string, boolean>;
  isBulkUpdatingAssignments: boolean;
  bulkUpdatingFieldsMap: Record<string, boolean>;
  repeatingDaysMap: Record<string, boolean>;
  isCreatingRepeatingAssignments: boolean;
  shouldScrollAssignmentList: boolean;
}

export interface CalendarFiltersState {
  students: DropdownOption<StudentOptionFragment>[];
  courses: DropdownOption<CourseOptionFragment>[];
  subjects: DropdownOption<string>[];
  assignmentTypes: DropdownOption<string>[];
  assignmentStatuses: DropdownOption<
    Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
  >[];
  hideCompleted: boolean;
}

export interface CalendarSelectionState {
  selectionMap: Record<string, DragAssignmentItem>;
  selectModeActive: boolean;
}

export interface CalendarState {
  view: CalendarView;
  currentWeek: string[];
  currentMonthAnchor: string;
  filters: CalendarFiltersState;
  selection: CalendarSelectionState;
}

export type CoursesView = 'grid' | 'list';
export type CoursesPageType = 'editable' | 'readonly' | 'organization_portal';

export interface FilterableCourse
  extends Omit<
    CourseCardInfoFragment,
    'copiedFromCourseId' | 'importedFromOrganizationId'
  > {
  copiedFromCourseId?: string | null;
  importedFromOrganizationId?: string | null;
  gradeYearsLabels: string[];
  studentNames: string[];
  organizationCourseGroups: Array<{
    organizationCourseGroup: {
      id: string;
      name: string;
    };
  }>;
}

export interface CoursesFiltersState {
  currentStatus?: DropdownOption<string>;
  currentEnrolledStudents?: DropdownOption<StudentOptionFragment>[];
  currentSubjects?: DropdownOption<string>[];
  currentLabels?: DropdownOption<string>[];
  currentGradeYears?: DropdownOption<string>[];
  currentOrganizationCourseGroups?: DropdownOption<string>[];
}

export interface BaseCoursesState {
  view: CoursesView;
  courses: FilterableCourse[];
  filters: CoursesFiltersState;
  searchQuery: string;
  hasSetDefaultFilters?: boolean;
  selectionMap: Record<string, boolean>;
}

export interface CoursesState {
  editable: BaseCoursesState;
  readonly: BaseCoursesState;
  organization_portal: BaseCoursesState;
}

export interface CoursesPayload<T> {
  type: CoursesPageType;
  value: T;
}

export interface TimeframeFilterState {
  preset: TimeframePreset;
  customStart: string | null;
  customEnd: string | null;
}

export default interface HomeFiltersState {
  students: DropdownOption<StudentOptionFragment>[];
  courses: DropdownOption<CourseOptionFragment>[];
  subjects: DropdownOption<string>[];
  assignmentTypes: DropdownOption<string>[];
  assignmentStatuses: DropdownOption<
    Student_Assignment_Status_Enum | CalculatedStudentAssignmentStatus
  >[];
  hideCompleted: boolean;
  timeframe: TimeframeFilterState;
}

export interface HomeState {
  filters: HomeFiltersState;
  homeStudentAssignments: HomeStudentAssignmentFragment[] | null;
  selectedHomeStudentAssignmentId: string | null;
  isDetailsSlideMenuOpen: boolean;
  isWelcomeToSyllabirdModalOpen: boolean;
}

export interface UserState {
  id: string;
  email: string;
  subscriptionStatus: string | null;
  planTier: Plan_Tier_Enum | null;
  role: UserRole | null;
  availableRoles: UserRole[];
  sessionId: string;
  settings: UserSettingsFragment;
  isIdentified: boolean;
  isInfoLoaded: boolean;
  organizationId: string | null;
  organizationName: string;
}

export interface RootState {
  home: HomeState;
  calendar: CalendarState;
  user: UserState;
  planning: PlanningState;
  courses: CoursesState;
}
